import { useState, useEffect } from "react";
import axios from "axios";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// SAP books NPD consumption onto a project TASK - there is no project-header
// booking (proven live 18 Oct 2026, document 302302 sits on COC10-T4). So once
// a project is picked, NPD picks the task the material is for. Tasks are
// grouped under the phase they belong to, and only Released ones are offered.
export const NpdTaskPicker = ({ projectId, value, onPick, testIdPrefix = "npd-task" }) => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!projectId) { setTasks([]); setError(null); return; }
    let dead = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/npd-store-requests/projects/${encodeURIComponent(projectId)}/tasks`);
        if (dead) return;
        setTasks(data.tasks || []);
        setError(null);
      } catch (e) {
        if (dead) return;
        setTasks([]);
        setError(e.response?.data?.detail || "Could not read the project's tasks from SAP");
      } finally {
        if (!dead) setLoading(false);
      }
    })();
    return () => { dead = true; };
  }, [projectId]);

  const grouped = tasks.reduce((acc, t) => {
    const key = t.phase_name || "Tasks";
    (acc[key] = acc[key] || []).push(t);
    return acc;
  }, {});

  if (!projectId) {
    return <p className="text-[11px] text-[#667085]" data-testid={`${testIdPrefix}-pick-project-first`}>Pick the project first.</p>;
  }

  return (
    <div>
      <Select value={value || ""} onValueChange={(v) => onPick(tasks.find((t) => t.task_id === v) || { task_id: v })}>
        <SelectTrigger className="h-9" data-testid={`${testIdPrefix}-trigger`}>
          <SelectValue placeholder={loading ? "Loading tasks from SAP..." : "Select the task"} />
        </SelectTrigger>
        <SelectContent>
          {Object.entries(grouped).map(([phase, rows]) => (
            <div key={phase}>
              <p className="px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-[#98A2B3]">{phase}</p>
              {rows.map((t) => (
                <SelectItem key={t.task_id} value={t.task_id} data-testid={`${testIdPrefix}-option-${t.task_id}`}>
                  <span className="font-mono text-[11px]">{t.task_id}</span>
                  <span className="ml-1.5">{t.name}</span>
                </SelectItem>
              ))}
            </div>
          ))}
        </SelectContent>
      </Select>
      {error && <p className="text-[11px] text-[#B42318] mt-1" data-testid={`${testIdPrefix}-error`}>{error}</p>}
      {!loading && !error && tasks.length === 0 && (
        <p className="text-[11px] text-[#B54708] mt-1" data-testid={`${testIdPrefix}-empty`}>
          This project has no released task in SAP - ask the project owner to release one before issuing material to it.
        </p>
      )}
    </div>
  );
};
